import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import LoadingSpinner from '../components/LoadingSpinner';
import { 
  Star, MapPin, Users, Zap, CheckCircle, Fuel, Gauge, Calendar,
  Shield, MessageCircle, Heart, Share2, ChevronLeft, ChevronRight
} from 'lucide-react';
import { cars as mockCars, mobilitiers } from '../mockData';

const CarDetails = () => {
  const { id } = useParams();
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    const fetchCar = async () => {
      setLoading(true);
      try {
        const { carsAPI } = await import('../api');
        const response = await carsAPI.getById(id);
        setCar(response.data);
        
        // Owner info is already included in response
        if (response.data.owner_name) {
          setOwner({
            name: response.data.owner_name,
            rating: response.data.owner_rating,
            trips: response.data.owner_trips,
            responseTime: response.data.owner_response_time || '2 horas',
            responseRate: response.data.owner_response_rate || 95
          });
        }
      } catch (error) {
        console.error('Error fetching car:', error);
        // Fallback to mock data
        const foundCar = mockCars.find(c => c.id === id);
        if (foundCar) {
          setCar(foundCar);
          const foundOwner = mobilitiers.find(m => m.id === foundCar.ownerId);
          setOwner(foundOwner);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchCar();
  }, [id]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % car.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + car.images.length) % car.images.length);
  };

  const handleBooking = () => {
    if (!user) {
      navigate('/login', { state: { from: `/car/${id}` } });
      return;
    }
    navigate(`/booking/${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (!car) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Auto no encontrado</h2>
          <Button onClick={() => navigate('/search')}>Volver a buscar</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Image Gallery */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="relative aspect-[16/9] md:aspect-[21/9] bg-gray-900">
            <img
              src={car.images[currentImageIndex]}
              alt={car.name}
              className="w-full h-full object-cover"
            />
            
            {/* Navigation Arrows */}
            {car.images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white"
                  onClick={prevImage}
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white"
                  onClick={nextImage}
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </>
            )}

            {/* Image Counter */}
            <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
              {currentImageIndex + 1} / {car.images.length}
            </div>
          </div>

          {/* Thumbnail Strip */}
          <div className="flex gap-2 p-4 overflow-x-auto">
            {car.images.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition ${
                  index === currentImageIndex ? 'border-[#000000]' : 'border-transparent'
                }`}
              >
                <img src={image} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title and Actions */}
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{car.name}</h1>
                  <div className="flex items-center gap-4 text-gray-600">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {car.location}
                    </div>
                    <div className="flex items-center">
                      <Star className="w-4 h-4 mr-1 fill-yellow-400 text-yellow-400" />
                      {car.rating} ({car.trips} {t('trips')})
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="icon" onClick={() => setIsFavorite(!isFavorite)}>
                    <Heart className={isFavorite ? 'fill-red-500 text-red-500' : ''} />
                  </Button>
                  <Button variant="outline" size="icon">
                    <Share2 />
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">{t(car.type.toLowerCase())}</Badge>
                <Badge variant="outline">{car.transmission}</Badge>
                <Badge variant="outline">
                  <Users className="w-3 h-3 mr-1" />
                  {car.capacity} {t('people')}
                </Badge>
                {car.instantBooking && (
                  <Badge className="bg-[#bcef3c] text-gray-900">
                    <Zap className="w-3 h-3 mr-1" />
                    {t('instantBooking')}
                  </Badge>
                )}
              </div>
            </div>

            {/* Description */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-3">{t('description')}</h2>
                <p className="text-gray-700 leading-relaxed">{car.description}</p>
              </CardContent>
            </Card>

            {/* Features */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-4">{t('features')}</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {car.features.map((feature, index) => (
                    <div key={index} className="flex items-center text-gray-700">
                      <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                      {feature}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Rental Details */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-4">Detalles de la renta</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center text-gray-700">
                    <Fuel className="w-5 h-5 mr-3 text-gray-400" />
                    <div>
                      <p className="font-medium">Política de combustible</p>
                      <p className="text-sm text-gray-600">Lleno a lleno</p>
                    </div>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <Gauge className="w-5 h-5 mr-3 text-gray-400" />
                    <div>
                      <p className="font-medium">Kilometraje incluido</p>
                      <p className="text-sm text-gray-600">{car.mileageLimit} km/día</p>
                    </div>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <Calendar className="w-5 h-5 mr-3 text-gray-400" />
                    <div>
                      <p className="font-medium">Duración mínima</p>
                      <p className="text-sm text-gray-600">{car.minDays} día(s)</p>
                    </div>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <Shield className="w-5 h-5 mr-3 text-gray-400" />
                    <div>
                      <p className="font-medium">Seguro incluido</p>
                      <p className="text-sm text-gray-600">Seguro del auto</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Owner Info */}
            {owner && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold mb-4">Anfitrion (Mobilitier)</h2>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <Avatar className="w-16 h-16">
                        <AvatarImage src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${owner.name}`} />
                        <AvatarFallback>{owner.name[0]}</AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-semibold text-lg">{owner.name}</h3>
                        <div className="flex items-center text-sm text-gray-600 mt-1">
                          <Star className="w-4 h-4 mr-1 fill-yellow-400 text-yellow-400" />
                          {owner.rating} • {owner.trips} viajes
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Tiempo de respuesta: {owner.responseTime}
                        </p>
                      </div>
                    </div>
                    {user && (
                      <Button variant="outline">
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Mensaje
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Booking Card */}
          <div className="lg:col-span-1">
            <Card className="sticky top-20">
              <CardContent className="p-6">
                <div className="mb-6">
                  <div className="text-3xl font-bold text-gray-900">
                    ${car.pricePerDay}
                    <span className="text-base font-normal text-gray-600 ml-2">MXN/{t('perDay')}</span>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Depósito (hold)</span>
                    <span className="font-medium">$500-1000 MXN</span>
                  </div>
                  {car.deliveryAvailable && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Entrega disponible</span>
                      <span className="font-medium">+${car.deliveryFee} MXN</span>
                    </div>
                  )}
                </div>

                <Button 
                  className="w-full bg-[#000000] hover:bg-ink-700 text-white font-semibold py-6 text-lg"
                  onClick={handleBooking}
                >
                  {t('bookNow')}
                </Button>

                <p className="text-xs text-center text-gray-500 mt-4">
                  No se te cobrará hasta que el Mobilitier apruebe tu reserva
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarDetails;